import * as React from "react";
import { useDispatch } from "react-redux";

import makeStyles from "@material-ui/styles/makeStyles";
import MuiDrawer from "@material-ui/core/Drawer";

import { ListItem, ListItemSecondaryAction, ListItemText, List as MuiList, Divider, Typography } from "@material-ui/core";

import IconButton from "@material-ui/core/IconButton";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import DeleteIcon from "@material-ui/icons/Delete";

import { Button, showNotification } from "jazasoft";

import { isEmpty } from "../../../utils/helpers";
import { useDeepMemo, useMutation, useTime } from "../../../hooks";
import { ReturnStatus, RollReturnTxnType } from "../../../utils/types";
import { isKnitRoll } from "../../../helpers/rollReturnHelper";
import { formatDateTime } from "../../../utils/dateUtils";

const useStyles = makeStyles(theme => ({
  drawerPaper: {
    maxWidth: 600
  },
  drawerHeader: {
    display: "flex",
    width: 480,
    alignItems: "center",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
    justifyContent: "flex-start"
  },
  content: {
    display: "flex",
    flex: 1,
    flexDirection: "column"
  },
  footer: {
    margin: "1.5em",
    display: "flex",
    justifyContent: "center"
  },
  label: {
    color: "rgba(0, 0, 0, 0.6)"
  }
}));

const RollReturnDetailsDrawer = props => {
  const classes = useStyles();
  const { open, cutOrderId, roll = {}, canDelete, onDrawerClose, onRefresh } = props;

  const [mutate, { loading: deleting }] = useMutation();
  const dispatch = useDispatch();
  const { utc_offset } = useTime();

  const rollReturn = roll?.rollReturn || {};

  const isKnit = isKnitRoll(roll);
  const isMissing = roll.txnType === RollReturnTxnType.MISSING;

  const onDelete = () => {
    const message = isMissing
      ? `Are you sure you want to delete the roll missing transaction of roll ${roll.rollNo}?`
      : `Are you sure you want to delete the roll return transaction of roll ${roll.rollNo}?`;

    // eslint-disable-next-line no-restricted-globals
    if (!confirm(message)) return;

    mutate(
      { url: `roll-return/${roll.rollReturnId}`, method: "delete", params: { cutOrderId } },
      {
        onSuccess: () => {
          dispatch(showNotification("Roll return transaction deleted successfully."));
          onRefresh && onRefresh();
          onDrawerClose && onDrawerClose();
        }
      }
    );
  };

  const listData = useDeepMemo(() => {
    if (isEmpty(roll)) return [];
    let list = [
      { label: "Factory Roll No.", value: roll.rollNo },
      { label: "Supplier Roll No", value: roll.supplierRollNo },
      { label: "Roll Type", value: roll.rollType },
      // { label: "Length", value: `${roll.length} m` },
      { label: "Weight", value: `${roll.weight} kg` },
      // {
      //   label: "Used",
      //   value: isKnit ? `${(roll.usedInLayWeight || 0) + (roll.usedInPanelWeight || 0)} kg` : `${(roll.usedInLay || 0) + (roll.usedInPanel || 0)} m`
      // }
    ];
    if (roll.txnType === RollReturnTxnType.MISSING) {
      list = [
        ...list,
        { label: "Missing Qty", value: isKnit ? `${roll.availableWeight} kg` : `${roll.available} m` },
        { label: "Missing Updated By", value: rollReturn?.createdBy },
        { label: "Missing Updated On", value: !isEmpty(utc_offset) ? formatDateTime(rollReturn?.createdAt, utc_offset) : "" },
        { label: "Remarks", value: roll.cuttingRemarks || "-" }
      ];
    } else {
      let returnQty = isKnit ? roll.availableWeight : roll.available;
      const confirmedQty = rollReturn?.state === ReturnStatus.CONFIRMED ? returnQty : undefined;
      if (rollReturn?.state === ReturnStatus.CONFIRMED) {
        returnQty = returnQty - (roll.returnShortageExcess || 0);
      }
      list = [
        ...list,
        { label: "Return Qty", value: `${returnQty} ${isKnit ? "kg" : "m"}` },
        { label: "Returned By", value: rollReturn?.createdBy },
        { label: "Returned At", value: !isEmpty(utc_offset) ? formatDateTime(rollReturn?.createdAt, utc_offset) : "" },
        { label: "Cutting Remarks", value: roll.cuttingRemarks || "-" }
      ];
      if (rollReturn?.state === ReturnStatus.CONFIRMED) {
        list = [
          ...list,
          { label: "Confirmed Qty", value: !isEmpty(confirmedQty) ? `${confirmedQty} ${isKnit ? "kg" : "m"}` : "" },
          { label: "Confirmed By", value: rollReturn?.lastModifiedBy },
          { label: "Confirmed At", value: !isEmpty(utc_offset) ? formatDateTime(rollReturn?.modifiedAt, utc_offset) : "" },
          { label: "Store Remarks", value: roll?.storeRemarks || "-" }
        ];
      }
    }
    if (!isKnit) {
      list = list.filter(e => e.label !== "Weight");
    }
    return list;
  }, [roll, utc_offset]);

  return (
    <MuiDrawer classes={{ paper: classes.drawerPaper }} anchor="right" open={open} onClose={onDrawerClose}>
      <div className={classes.drawerHeader}>
        <IconButton onClick={onDrawerClose}>
          <ChevronRightIcon />
        </IconButton>
        <div>
          <Typography variant="h5">Roll Transaction History</Typography>
        </div>
      </div>
      <Divider />

      <div className={classes.content}>
        <div>
          <MuiList disablePadding>
            {listData.map(({ label, value }, idx) => (
              <ListItem key={idx} divider>
                <ListItemText primary={label} />
                <ListItemSecondaryAction>
                  <ListItemText primary={value} />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </MuiList>
        </div>

        <div style={{ display: "flex", flex: 1, flexGrow: 1 }} />

        {canDelete && rollReturn?.state !== ReturnStatus.CONFIRMED && (
          <div className={classes.footer}>
            <Button
              style={{ marginLeft: "1em", color: "red", borderColor: "red" }}
              label={"Delete"}
              variant="outlined"
              disabled={deleting}
              onClick={onDelete}>
              <DeleteIcon />
            </Button>
          </div>
        )}
      </div>
    </MuiDrawer>
  );
};

export default RollReturnDetailsDrawer;
